import { useMemo, useState } from "react";
import { Box, Button, IconButton, Switch, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import moment from "moment";
import { toast } from "react-toastify";
import RecipeDetailModal from "../modal/newDiet/RecipeDetailModal";
import RecipeModal from "../modal/newDiet/RecipeModal";
import WeeklyRecipePickerDialog from "./WeeklyRecipePickerDialog";
import { enumerateDatesInclusive } from "utils/perDayDietChart";
import {
  WEEKLY_MATRIX_SCHEDULE_COL_PX,
  WEEKLY_MATRIX_DAY_COL_PX,
  weeklyMatrixTableWidthPx,
} from "utils/weeklyPerDayMatrixSx";

function createRecipeInstanceId(recipeId) {
  return `${recipeId || "recipe"}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function getScheduleLabel(scheduleKey, defaultSchedules, customSchedules) {
  const d = defaultSchedules.find((s) => s.id === scheduleKey);
  if (d) return d.title;
  const c = customSchedules.find((s) => s.normalizedId === scheduleKey);
  return c?.title || scheduleKey;
}

function getScheduleTime(scheduleKey, defaultSchedules, customSchedules) {
  const d = defaultSchedules.find((s) => s.id === scheduleKey);
  if (d) return d.time;
  const c = customSchedules.find((s) => s.normalizedId === scheduleKey);
  return c?.time || "12:00";
}

function normalizeTimeValue(rawTime, fallbackTime = "12:00") {
  const parsed = moment(rawTime, ["HH:mm", "H:mm", "hh:mm A", "h:mm A"], true);
  if (parsed.isValid()) return parsed.format("HH:mm");

  const fallbackParsed = moment(
    fallbackTime,
    ["HH:mm", "H:mm", "hh:mm A", "h:mm A"],
    true
  );
  if (fallbackParsed.isValid()) return fallbackParsed.format("HH:mm");
  return "12:00";
}

export default function WeeklyPerDayMatrix({
  from,
  to,
  dayPlans,
  setDayPlans,
  schedules,
  customSchedules,
  defaultMealSchedules,
  mode,
  uid,
  onPersistDayPlans,
}) {
  const theme = useTheme();
  const dates = useMemo(
    () => (from && to ? enumerateDatesInclusive(from, to) : []),
    [from, to]
  );

  const scheduleKeys = useMemo(() => {
    const keys = [];
    for (const s of schedules || []) {
      const k = s.normalizedId || s.id;
      if (k) keys.push(k);
    }
    for (const c of customSchedules || []) {
      if (c.normalizedId && !keys.includes(c.normalizedId)) {
        keys.push(c.normalizedId);
      }
    }
    return keys;
  }, [schedules, customSchedules]);

  const [recipeDetailOpen, setRecipeDetailOpen] = useState(false);
  const [recipePickerOpen, setRecipePickerOpen] = useState(false);
  const [recipeEditOpen, setRecipeEditOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [editRecipeItem, setEditRecipeItem] = useState(null);
  const [activeCell, setActiveCell] = useState({ date: null, scheduleKey: null });
  const [chartEditCell, setChartEditCell] = useState(null);

  const readOnly = mode === "view";

  const appendRecipe = (recipe) => {
    const { date, scheduleKey } = activeCell;
    if (!date || !scheduleKey) return;
    const scheduleTime = getScheduleTime(
      scheduleKey,
      defaultMealSchedules,
      customSchedules
    );
    const recipeWithTime = {
      ...recipe,
      instanceId: recipe.instanceId || createRecipeInstanceId(recipe.id),
      time: normalizeTimeValue(scheduleTime, "12:00"),
      toggleMode: "AND",
    };
    setDayPlans((prev) => {
      const day = { ...(prev[date] || {}) };
      const list = [...(day[scheduleKey] || [])];
      if (list.some((r) => r.id === recipeWithTime.id)) {
        toast.error(`${recipe.name} is already added for this slot.`);
        return prev;
      }
      if (list.length >= 50) {
        toast.error("Max 50 items per slot for this day.");
        return prev;
      }
      day[scheduleKey] = [...list, recipeWithTime];
      return { ...prev, [date]: day };
    });
    setRecipePickerOpen(false);
  };

  const removeRecipe = (date, scheduleKey, recipeIndex) => {
    setDayPlans((prev) => {
      const day = { ...(prev[date] || {}) };
      const list = (day[scheduleKey] || []).filter(
        (_, index) => index !== recipeIndex
      );
      if (list.length) day[scheduleKey] = list;
      else delete day[scheduleKey];
      const next = { ...prev };
      if (Object.keys(day).length) next[date] = day;
      else delete next[date];
      return next;
    });
  };

  const updateRecipeInCell = (date, scheduleKey, recipeIndex, updates) => {
    setDayPlans((prev) => {
      const day = { ...(prev[date] || {}) };
      const list = [...(day[scheduleKey] || [])];
      if (recipeIndex < 0 || recipeIndex >= list.length) return prev;
      list[recipeIndex] = { ...list[recipeIndex], ...updates };
      day[scheduleKey] = list;
      return { ...prev, [date]: day };
    });
  };

  const openAdd = (date, scheduleKey) => {
    setActiveCell({ date, scheduleKey });
    setRecipePickerOpen(true);
  };

  const openRecipeDetail = (date, scheduleKey, recipe, recipeIndex) => {
    setChartEditCell({ date, scheduleKey, recipeIndex });
    setActiveCell({ date, scheduleKey });
    setSelectedRecipe(recipe);
    setRecipeDetailOpen(true);
  };

  const handleChartRecipeEdit = (recipe) => {
    if (!chartEditCell?.date || !recipe?.id) return;
    setRecipeDetailOpen(false);
    setEditRecipeItem({
      ...recipe,
      goesWell: recipe.goesWell?.length ? recipe.goesWell : [chartEditCell.scheduleKey],
      qty: recipe.qty ?? 1,
    });
    setRecipeEditOpen(true);
  };

  const handleChartRecipeSave = async (formData) => {
    if (chartEditCell?.recipeIndex === undefined) return;
    const { date, scheduleKey, recipeIndex } = chartEditCell;
    const name =
      typeof formData.name === "object"
        ? formData.name.label || formData.name.value
        : formData.name;
    let nextPlans = dayPlans;
    setDayPlans((prev) => {
      const day = { ...(prev[date] || {}) };
      const list = [...(day[scheduleKey] || [])];
      if (recipeIndex < 0 || recipeIndex >= list.length) return prev;
      list[recipeIndex] = {
        ...list[recipeIndex],
        ...formData,
        name,
        unit: formData.unit?.value ?? formData.unit,
      };
      nextPlans = { ...prev, [date]: { ...day, [scheduleKey]: list } };
      return nextPlans;
    });
    try {
      await onPersistDayPlans?.(nextPlans);
    } catch (e) {
      toast.error(e?.message || "Could not save.");
      return;
    }
    setRecipeEditOpen(false);
    setEditRecipeItem(null);
    setChartEditCell(null);
    setSelectedRecipe(null);
    toast.success("Recipe saved for this client's diet.");
  };

  const tableWidthPx = weeklyMatrixTableWidthPx(dates.length);

  const dayCellSx = {
    width: WEEKLY_MATRIX_DAY_COL_PX,
    minWidth: WEEKLY_MATRIX_DAY_COL_PX,
    maxWidth: WEEKLY_MATRIX_DAY_COL_PX,
    boxSizing: "border-box",
    verticalAlign: "top",
    px: 1,
    py: 1,
  };

  const addButtonSx = {
    mt: "auto",
    textTransform: "none",
    fontWeight: 600,
    py: 0.5,
    borderColor: theme.palette.info.main,
    color: theme.palette.info.main,
    boxShadow: "none",
    "&:hover": {
      borderColor: theme.palette.info.dark,
      color: theme.palette.info.dark,
      backgroundColor: `${theme.palette.info.main}14`,
      boxShadow: "none",
    },
  };

  /** Sticky first column: flex + sticky is reliable; table-cell sticky is inconsistent in browsers. */
  const stickyScheduleCell = {
    position: "sticky",
    left: 0,
    flex: `0 0 ${WEEKLY_MATRIX_SCHEDULE_COL_PX}px`,
    width: WEEKLY_MATRIX_SCHEDULE_COL_PX,
    minWidth: WEEKLY_MATRIX_SCHEDULE_COL_PX,
    maxWidth: WEEKLY_MATRIX_SCHEDULE_COL_PX,
    boxSizing: "border-box",
    px: 1.5,
    py: 1.25,
    borderRight: "1px solid",
    borderColor: "divider",
    backgroundColor: theme.palette.grey[50],
    backgroundClip: "padding-box",
    boxShadow: "4px 0 12px -6px rgba(0,0,0,0.15)",
  };

  const dayHeaderCellSx = {
    ...dayCellSx,
    position: "sticky",
    top: 0,
    zIndex: 3,
    bgcolor: "grey.100",
    borderBottom: "1px solid",
    borderColor: "divider",
    py: 1.5,
    flex: `0 0 ${WEEKLY_MATRIX_DAY_COL_PX}px`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  };

  return (
    <>
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "visible",
        WebkitOverflowScrolling: "touch",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box
        role="table"
        aria-label="Weekly per-day meal schedule"
        sx={{
          display: "flex",
          flexDirection: "column",
          width: tableWidthPx,
          minWidth: tableWidthPx,
        }}
      >
        <Box
          component="div"
          role="row"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            position: "sticky",
            top: 0,
            zIndex: 3,
            bgcolor: "grey.100",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            component="div"
            role="columnheader"
            sx={{
              ...stickyScheduleCell,
              zIndex: 5,
              top: 0,
              py: 1.5,
              bgcolor: "grey.100",
              fontWeight: 700,
              fontSize: "0.8125rem",
              color: "text.primary",
            }}
          >
            Schedule
          </Box>
          {dates.map((d) => (
            <Box
              key={d}
              component="div"
              role="columnheader"
              sx={dayHeaderCellSx}
            >
              <Typography
                variant="caption"
                component="div"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  lineHeight: 1.3,
                  display: "block",
                }}
              >
                {moment(d, "YYYY-MM-DD").format("ddd")}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}
              >
                {moment(d, "YYYY-MM-DD").format("MMM D")}
              </Typography>
            </Box>
          ))}
        </Box>

        {scheduleKeys.map((sk) => (
          <Box
            key={sk}
            component="div"
            role="row"
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box
              component="div"
              role="rowheader"
              sx={{
                ...stickyScheduleCell,
                zIndex: 2,
                fontWeight: 600,
                fontSize: "0.8125rem",
                color: "text.primary",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {getScheduleLabel(sk, defaultMealSchedules, customSchedules)}
            </Box>
            {dates.map((d) => {
              const items = (dayPlans[d] && dayPlans[d][sk]) || [];
              return (
                <Box
                  key={`${d}-${sk}`}
                  component="div"
                  role="gridcell"
                  sx={{
                    ...dayCellSx,
                    flex: `0 0 ${WEEKLY_MATRIX_DAY_COL_PX}px`,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        justifyContent: "flex-start",
                      }}
                    >
                      {items.map((r, recipeIndex) => (
                        <Box key={`${r.instanceId || r.id}-${d}-${sk}-${recipeIndex}`}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "stretch",
                              gap: 0.35,
                              py: 0.25,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              px: 0.75,
                              flexDirection: "column",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  wordBreak: "break-word",
                                  cursor: readOnly ? "default" : "pointer",
                                  color: "text.primary",
                                }}
                                onClick={() => {
                                  if (readOnly) return;
                                  openRecipeDetail(d, sk, r, recipeIndex);
                                }}
                              >
                                {r.name}
                              </Typography>
                              {!readOnly && (
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.25, mt: -0.25 }}
                                  onClick={() => removeRecipe(d, sk, recipeIndex)}
                                  aria-label="remove"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Time
                              </Typography>
                              <input
                                type="time"
                                disabled={readOnly}
                                value={normalizeTimeValue(
                                  r.time,
                                  getScheduleTime(sk, defaultMealSchedules, customSchedules)
                                )}
                                onChange={(event) =>
                                  updateRecipeInCell(d, sk, recipeIndex, {
                                    time: normalizeTimeValue(
                                      event.target.value,
                                      getScheduleTime(
                                        sk,
                                        defaultMealSchedules,
                                        customSchedules
                                      )
                                    ),
                                  })
                                }
                                style={{
                                  width: 92,
                                  fontSize: 11,
                                  border: "1px solid #D0D5DD",
                                  borderRadius: 6,
                                  padding: "2px 6px",
                                  backgroundColor: readOnly ? "#F9FAFB" : "#FFFFFF",
                                }}
                              />
                            </Box>
                          </Box>

                          {recipeIndex < items.length - 1 && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.3,
                                py: 0.15,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {r.toggleMode === "OR" ? "OR" : "AND"}
                              </Typography>
                              <Switch
                                disabled={readOnly}
                                checked={r.toggleMode === "OR"}
                                onChange={(event) =>
                                  updateRecipeInCell(d, sk, recipeIndex, {
                                    toggleMode: event.target.checked ? "OR" : "AND",
                                  })
                                }
                                sx={{
                                  width: 34,
                                  height: 20,
                                  padding: 0,
                                  margin: 0,
                                  flexShrink: 0,
                                  // Soft UI theme uses top:50% + translate(y:-50%).
                                  // Must keep the Y offset when checked or the thumb drops.
                                  "& .MuiSwitch-switchBase": {
                                    padding: 0,
                                    margin: 0,
                                    top: "50%",
                                    transform: "translate(2px, -50%)",
                                    transitionDuration: "150ms",
                                    "&.Mui-checked": {
                                      transform: "translate(16px, -50%)",
                                      color: "#fff",
                                      "& + .MuiSwitch-track": {
                                        opacity: 1,
                                      },
                                    },
                                  },
                                  "& .MuiSwitch-thumb": {
                                    width: 16,
                                    height: 16,
                                    boxShadow: "none",
                                  },
                                  "& .MuiSwitch-track": {
                                    borderRadius: 10,
                                    opacity: 1,
                                    backgroundColor: "#E9E9EA",
                                    boxSizing: "border-box",
                                  },
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      ))}
                      {!readOnly && (
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => openAdd(d, sk)}
                          disableElevation
                          sx={addButtonSx}
                        >
                          Add
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
      </Box>
    </Box>

      <WeeklyRecipePickerDialog
        open={recipePickerOpen}
        onClose={() => setRecipePickerOpen(false)}
        uid={uid}
        activeSchedule={activeCell.scheduleKey}
        defaultMealSchedules={defaultMealSchedules}
        customSchedules={customSchedules}
        onSelectRecipe={(recipe) => {
          appendRecipe(recipe);
        }}
      />

      {selectedRecipe && (
        <RecipeDetailModal
          open={recipeDetailOpen}
          onClose={() => {
            setRecipeDetailOpen(false);
            setSelectedRecipe(null);
          }}
          recipe={selectedRecipe}
          onAdd={appendRecipe}
          onEdit={readOnly ? undefined : handleChartRecipeEdit}
          onDelete={
            readOnly
              ? undefined
              : () => {
                  if (chartEditCell?.recipeIndex !== undefined) {
                    removeRecipe(
                      chartEditCell.date,
                      chartEditCell.scheduleKey,
                      chartEditCell.recipeIndex
                    );
                  }
                  setRecipeDetailOpen(false);
                  setSelectedRecipe(null);
                  setChartEditCell(null);
                }
          }
        />
      )}

      <RecipeModal
        open={recipeEditOpen}
        onClose={() => {
          setRecipeEditOpen(false);
          setEditRecipeItem(null);
        }}
        selectedItem={editRecipeItem}
        mode="edit"
        setMode={() => {}}
        setSelectedItem={setEditRecipeItem}
        editHandler={() => {}}
        viewHandler={() => {}}
        defaultMealSchedules={defaultMealSchedules}
        customSchedules={customSchedules}
        onLocalSave={handleChartRecipeSave}
      />
    </>
  );
}
